import { Fragment, Suspense } from 'react';
import { DataTestId, FeatureFlag, TextKey } from '@mews-commander/enums';
import { IconName, Tag, Stack, ExpandableList, SkeletonText, Container } from '@optimus-web/core';
import { lt, LocalizedText, useFeatureFlag, useLocalizeText } from '@mews/framework';
import { OOTB_DASHBOARDS } from '@mews-commander/business-intelligence/const';
import { useDashboards } from '@mews-commander/business-intelligence/hooks';
import { MewsBiUserOnboardingStatusEnum, useRepositoryQuery } from '@mews-commander/data-access';
import { canCurrentProfileViewSensitiveReports } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { createListItem, createListItemWithLink } from '../helpers';
import { SidebarExpandableListItem, SidebarFontIcon } from '../style';

const EMPTY_ICON = (<></>) as unknown as IconName;

interface BIMenuItemProps {
    value: string;
    opened?: boolean;
    onClicked?: () => void;
    depth?: number;
}

// This disconnects the sub-expandable items from the top level resulting in correct indentation
const BIMenuItemWrapper = ({ children }: React.PropsWithChildren<{}>) => {
    return <ExpandableList>{children}</ExpandableList>;
};

const BICustomDashboardsMenu = (props: BIMenuItemProps) => {
    const makeSkeletonItem = () => {
        return createListItem({
            label: <SkeletonText width="100%" />,
            iconName: EMPTY_ICON,
        });
    };
    return (
        <Suspense
            fallback={
                <SidebarExpandableListItem
                    leftIcon={<SidebarFontIcon value={EMPTY_ICON} />}
                    primaryText={lt('MewsBICustomDashboards')}
                    {...props}
                >
                    {makeSkeletonItem()}
                    {makeSkeletonItem()}
                    {makeSkeletonItem()}
                    {makeSkeletonItem()}
                </SidebarExpandableListItem>
            }
        >
            <BICustomDashboardsItems {...props} />
        </Suspense>
    );
};

const BICustomDashboardsItems = (props: BIMenuItemProps) => {
    const dashboards = useDashboards();

    const ootbDashboards = OOTB_DASHBOARDS.map((dashboard) => dashboard.id) as string[];
    const customDashboards = dashboards.filter((dashboard) => !ootbDashboards.includes(dashboard.id));

    return (
        <SidebarExpandableListItem
            leftIcon={<SidebarFontIcon value={EMPTY_ICON} />}
            primaryText={lt('MewsBICustomDashboards')}
            {...props}
        >
            {customDashboards.length ? (
                customDashboards.map((dashboard) => (
                    <Fragment key={dashboard.id}>
                        {createListItemWithLink({
                            dataTestId: `${DataTestId.BIMenuItemCustomDashboard}-${dashboard.id}`,
                            text: dashboard.name,
                            path: 'BusinessIntelligence/Dashboard/$dashboardId',
                            params: { dashboardId: dashboard.id },
                            iconName: EMPTY_ICON,
                        })}
                    </Fragment>
                ))
            ) : (
                <Container />
            )}
        </SidebarExpandableListItem>
    );
};

const BICuratedDashboardsMenu = (props: BIMenuItemProps) => {
    const localizeText = useLocalizeText();
    return (
        <SidebarExpandableListItem
            leftIcon={<SidebarFontIcon value={EMPTY_ICON} />}
            primaryText={lt('MewsBICuratedDashboards')}
            {...props}
        >
            {OOTB_DASHBOARDS.map((dashboard) => (
                <Fragment key={dashboard.id}>
                    {createListItemWithLink({
                        dataTestId: `${DataTestId.BIMenuItemCuratedDashboard}-${dashboard.id}`,
                        text: localizeText({ textKey: dashboard.name }),
                        path: 'BusinessIntelligence/Dashboard/$dashboardId',
                        params: { dashboardId: dashboard.id },
                        iconName: EMPTY_ICON,
                    })}
                </Fragment>
            ))}
        </SidebarExpandableListItem>
    );
};

export const BIMenuItem = (props: BIMenuItemProps) => {
    const canViewSensitiveReports = useCommanderSelector(canCurrentProfileViewSensitiveReports);
    const insightsEnabled = useFeatureFlag(FeatureFlag.insightsEnabled);
    const canAccessAllEnterprises = useCommanderSelector(hasPlatformScopeWithAccessAllEnterprises);
    const mewsBiRedesignMenu = useFeatureFlag(FeatureFlag.mewsBiRedesignMenu);
    const { data } = useRepositoryQuery('goodData', 'getParameters', {});
    const BIEnabled =
        data?.biOnboardingStatus !== MewsBiUserOnboardingStatusEnum.NotFound ||
        insightsEnabled ||
        canAccessAllEnterprises;

    if (!canViewSensitiveReports || !BIEnabled || !data?.biWorkspaceId) return null;

    if (!mewsBiRedesignMenu) {
        return createListItemWithLink({
            text: 'Mews BI',
            iconName: IconName.PowerBi,
            path: 'BusinessIntelligence',
            isMainItem: true,
            label: (
                <Tag variant="primary">
                    <LocalizedText textKey={TextKey.Beta} />
                </Tag>
            ),
        });
    }

    return (
        <SidebarExpandableListItem
            leftIcon={<SidebarFontIcon value={IconName.PowerBi} />}
            primaryText={
                <Stack wrap spacing="200" itemAlignment="center">
                    Mews BI
                    <Tag variant="primary">
                        <LocalizedText textKey={TextKey.Beta} />
                    </Tag>
                </Stack>
            }
            {...props}
        >
            <BIMenuItemWrapper>
                {createListItemWithLink({
                    dataTestId: DataTestId.BIMenuItemOverview,
                    textKey: TextKey.Overview,
                    path: 'BusinessIntelligence',
                    iconName: EMPTY_ICON,
                })}
            </BIMenuItemWrapper>
            <BIMenuItemWrapper>
                <BICuratedDashboardsMenu value="mews-bi-curated-dashboards" />
            </BIMenuItemWrapper>

            <BIMenuItemWrapper>
                <BICustomDashboardsMenu value="mews-bi-custom-dashboards" />
            </BIMenuItemWrapper>
        </SidebarExpandableListItem>
    );
};

export default BIMenuItem;
